<section
  class="ui-list-report"
  [attr.aria-label]="title()"
  [style.--ui-list-report-height.px]="reportHeight()"
>
  @if (showTitle()) {
    <header class="ui-list-report__header">
      <h3 class="ui-list-report__title">{{ title() }}</h3>
      @if (showMore()) {
        <ui-button variant="ghost" size="sm" (pressed)="moreClick.emit($event)">
          {{ moreLabel() }}
        </ui-button>
      }
    </header>

    <div class="ui-list-report__divider" aria-hidden="true"></div>
  }

  <div
    class="ui-list-report__viewport"
    [class.ui-list-report__viewport--vertical]="hasVerticalOverflow()"
    [class.ui-list-report__viewport--fit]="tableWidth() === '100%'"
    [class.ui-list-report__viewport--horizontal-scrollbar]="
      showHorizontalScrollbar()
    "
    [style.height.px]="viewportHeight()"
  >
    <table
      class="ui-list-report__table"
      [class.ui-list-report__table--ready]="columnsReady()"
      [style.width]="tableWidth()"
    >
      <colgroup>
        @for (column of columns(); track column.key) {
          <col [style.width]="colWidths()[column.key]" />
        }
        @if (actions().length) {
          <col style="width: 48px" />
        }
      </colgroup>

      <thead>
        <tr class="ui-list-report__columns">
          @for (column of columns(); track column.key; let columnIndex = $index) {
            <th
              class="ui-list-report__column"
              [class.ui-list-report__column--end]="column.align === 'end'"
              [class.ui-list-report__column--emphasis]="column.emphasis"
              [class.ui-list-report__column--truncate]="column.truncate"
              [class.ui-list-report__column--sortable]="
                isColumnSortable(column)
              "
              [class.ui-list-report__column--sorted]="
                isColumnSortable(column) && sortState()?.sortBy === column.key
              "
              [class.ui-list-report__sticky]="isStickyColumn(columnIndex)"
              [class.ui-list-report__sticky--edge]="isStickyEdge(columnIndex)"
              [class.ui-list-report__sticky-end]="isStickyEnd(columnIndex)"
              [style.left.px]="stickyLeft(columnIndex)"
              [style.right.px]="stickyEnd(columnIndex)"
              scope="col"
              [attr.aria-sort]="ariaSort(column)"
              (click)="onHeaderClick(column)"
            >
              <span class="ui-list-report__column-label">
                <span class="ui-list-report__column-text">{{
                  column.label
                }}</span>
                @if (isColumnSortable(column)) {
                  <span class="ui-list-report__sort-icon" aria-hidden="true">
                    @if (
                      sortState()?.sortBy === column.key &&
                      sortState()?.order === 'asc'
                    ) {
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      >
                        <polyline points="18 15 12 9 6 15" />
                      </svg>
                    } @else if (
                      sortState()?.sortBy === column.key &&
                      sortState()?.order === 'desc'
                    ) {
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="2.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      >
                        <polyline points="6 9 12 15 18 9" />
                      </svg>
                    } @else {
                      <svg
                        width="12"
                        height="12"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#cbd5e1"
                        stroke-width="2.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      >
                        <polyline points="6 9 12 15 18 9" />
                      </svg>
                    }
                  </span>
                }
              </span>
            </th>
          }
          @if (actions().length) {
            <th
              class="ui-list-report__column ui-list-report__column--actions"
              scope="col"
              aria-label="Acciones"
            ></th>
          }
        </tr>

        @if (showColumnFilters()) {
          <tr class="ui-list-report__filter-row">
            @for (column of columns(); track column.key; let columnIndex = $index) {
              <th
                class="ui-list-report__filter-cell"
                [class.ui-list-report__filter-cell--open]="
                  openFilterKey() === column.key
                "
                [class.ui-list-report__filter-cell--emphasis]="column.emphasis"
                [class.ui-list-report__sticky]="isStickyColumn(columnIndex)"
                [class.ui-list-report__sticky--edge]="isStickyEdge(columnIndex)"
                [class.ui-list-report__sticky-end]="isStickyEnd(columnIndex)"
                [style.left.px]="stickyLeft(columnIndex)"
                [style.right.px]="stickyEnd(columnIndex)"
              >
                @if (column.filterType === 'select') {
                  <ui-select
                    class="ui-list-report__filter-select"
                    [options]="filterOptionsOf(column)"
                    [selected]="filterSelectedOption(column)"
                    [showLabel]="false"
                    [showOptionalHint]="false"
                    [showMessage]="false"
                    [showSearch]="false"
                    [block]="true"
                    [fitPanelToContent]="true"
                    placeholder="Todos"
                    (opened)="openFilterKey.set(column.key)"
                    (closed)="closeFilter(column.key)"
                    (selectedChange)="onColumnFilterSelect(column.key, $event)"
                  />
                } @else if (column.filterType === 'multiselect') {
                  <ui-multiselect
                    class="ui-list-report__filter-multiselect"
                    [options]="filterOptionsOf(column)"
                    [selected]="filterSelectedMultiOptions(column)"
                    [showLabel]="false"
                    [showOptionalHint]="false"
                    [showMessage]="false"
                    [block]="true"
                    [fitPanelToContent]="true"
                    [maxChipsVisible]="1"
                    placeholder="Todos"
                    (opened)="openFilterKey.set(column.key)"
                    (closed)="closeFilter(column.key)"
                    (selectedChange)="onColumnFilterMulti(column.key, $event)"
                  />
                } @else if (column.filterType !== 'none') {
                  <input
                    class="ui-list-report__filter-input"
                    type="text"
                    placeholder="Buscar..."
                    [value]="filterValue(column.key)"
                    (input)="
                      onColumnFilter(column.key, $any($event.target).value)
                    "
                  />
                }
              </th>
            }
            @if (actions().length) {
              <th
                class="ui-list-report__filter-cell ui-list-report__filter-cell--actions"
              ></th>
            }
          </tr>
        }
      </thead>

      <tbody>
        @if (showEmptyState() && displayRows().length === 0) {
          <tr>
            <td
              class="ui-list-report__empty"
              [attr.colspan]="columns().length + (actions().length ? 1 : 0)"
            >
              <svg
                width="32"
                height="32"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#cbd5e1"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              {{ emptyText() }}
            </td>
          </tr>
        } @else {
          @for (entry of displayRows(); track entry.id; let index = $index) {
            <tr
              class="ui-list-report__row"
              [class.ui-list-report__row--open]="entry.expanded"
              [class.ui-list-report__row--emphasis]="isEmphasizedRow(entry.row)"
              [class.ui-list-report__row--menu-open]="menu()?.rowIndex === index"
              [class.ui-list-report__row--clickable]="clickableRows()"
              (click)="onRowClick(entry.row, $event)"
            >
              @for (column of columns(); track column.key; let columnIndex = $index) {
                <td
                  class="ui-list-report__cell"
                  [class.ui-list-report__cell--end]="column.align === 'end'"
                  [class.ui-list-report__cell--truncate]="column.truncate"
                  [class.ui-list-report__cell--emphasis]="column.emphasis"
                  [class.ui-list-report__cell--success]="column.valueTone === 'success'"
                  [class.ui-list-report__cell--danger]="column.valueTone === 'danger'"
                  [class.ui-list-report__sticky]="isStickyColumn(columnIndex)"
                  [class.ui-list-report__sticky--edge]="isStickyEdge(columnIndex)"
                  [class.ui-list-report__sticky-end]="isStickyEnd(columnIndex)"
                  [style.left.px]="stickyLeft(columnIndex)"
                  [style.right.px]="stickyEnd(columnIndex)"
                >
                  @if (columnIndex === 0 && hasExpandableRows()) {
                    <span
                      class="ui-list-report__tree"
                      [style.padding-left.px]="treeIndent(entry.level)"
                    >
                      @if (entry.expandable) {
                        <button
                          type="button"
                          class="ui-list-report__toggle"
                          [attr.aria-expanded]="entry.expanded"
                          [attr.aria-label]="
                            entry.expanded ? 'Ocultar detalle' : 'Mostrar detalle'
                          "
                          (click)="toggleExpanded($event, entry.id)"
                        >
                          <svg
                            class="ui-list-report__toggle-icon"
                            [class.ui-list-report__toggle-icon--open]="
                              entry.expanded
                            "
                            width="12"
                            height="12"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2.5"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            aria-hidden="true"
                          >
                            <polyline points="9 6 15 12 9 18" />
                          </svg>
                        </button>
                      } @else {
                        <span
                          class="ui-list-report__toggle-spacer"
                          aria-hidden="true"
                        ></span>
                      }
                      @if (column.tag) {
                        <ui-tag
                          [label]="cellText(column, entry.row)"
                          [color]="tagColor(column, entry.row)"
                          [appearance]="column.tagAppearance ?? 'filled'"
                        />
                      } @else {
                        <span class="ui-list-report__tree-label">
                          @if (cellSwatch(column, entry.row); as swatch) {
                            <span
                              class="ui-list-report__swatch"
                              [style.background]="swatch"
                              aria-hidden="true"
                            ></span>
                          }
                          {{ cellText(column, entry.row) }}
                        </span>
                      }
                    </span>
                  } @else if (column.tag) {
                    <ui-tag
                      [label]="cellText(column, entry.row)"
                      [color]="tagColor(column, entry.row)"
                      [appearance]="column.tagAppearance ?? 'filled'"
                    />
                  } @else {
                    <span class="ui-list-report__value">
                      @if (cellSwatch(column, entry.row); as swatch) {
                        <span
                          class="ui-list-report__swatch"
                          [style.background]="swatch"
                          aria-hidden="true"
                        ></span>
                      }
                      {{ cellText(column, entry.row) }}
                    </span>
                  }
                </td>
              }
              @if (actions().length) {
              <td
                  class="ui-list-report__actions"
                  [class.ui-list-report__actions--open]="
                    menu()?.rowIndex === index
                  "
                >
                  <button
                    type="button"
                    class="ui-list-report__actions-trigger"
                    aria-label="Acciones"
                    [attr.aria-expanded]="menu()?.rowIndex === index"
                    (click)="openActions($event, entry.row, index)"
                  >
                    ⋮
                  </button>
                </td>
              }
            </tr>
          }
          @for (_ of fillerRows(); track $index) {
            <tr class="ui-list-report__row ui-list-report__row--filler">
              @for (column of columns(); track column.key; let columnIndex = $index) {
                <td
                  class="ui-list-report__cell"
                  [class.ui-list-report__cell--emphasis]="column.emphasis"
                  [class.ui-list-report__sticky]="isStickyColumn(columnIndex)"
                  [class.ui-list-report__sticky--edge]="isStickyEdge(columnIndex)"
                  [class.ui-list-report__sticky-end]="isStickyEnd(columnIndex)"
                  [style.left.px]="stickyLeft(columnIndex)"
                  [style.right.px]="stickyEnd(columnIndex)"
                ></td>
              }
              @if (actions().length) {
                <td class="ui-list-report__actions"></td>
              }
            </tr>
          }
        }
      </tbody>

      @if (showTotals() && (!showEmptyState() || displayRows().length > 0)) {
        <tfoot>
          <tr class="ui-list-report__total-row">
            @for (column of columns(); track column.key; let index = $index) {
              <td
                class="ui-list-report__total-cell"
                [class.ui-list-report__total-cell--end]="column.align === 'end'"
                [class.ui-list-report__total-cell--emphasis]="column.emphasis"
                [class.ui-list-report__total-cell--truncate]="column.truncate"
                [class.ui-list-report__sticky]="isStickyColumn(index)"
                [class.ui-list-report__sticky--edge]="isStickyEdge(index)"
                [class.ui-list-report__sticky-end]="isStickyEnd(index)"
                [style.left.px]="stickyLeft(index)"
                [style.right.px]="stickyEnd(index)"
              >
                {{ totalCellText(column, index) }}
              </td>
            }
            @if (actions().length) {
              <td
                class="ui-list-report__total-cell ui-list-report__total-cell--actions"
              ></td>
            }
          </tr>
        </tfoot>
      }
    </table>
  </div>
</section>

@if (menu(); as menuState) {
  <div class="ui-list-report__backdrop" (click)="closeActions()"></div>
  <ui-data-table-actions-menu
    [state]="menuState"
    [items]="menuState.items"
    (actionSelect)="selectAction($event)"
  />
}
